import type { ComputedRef, Ref } from 'vue';
import { computed, ref } from 'vue';

import type { ITableMultiselectActionParams } from '@modules/tables/components/VTable/types';
import type { ITableFilterProps } from '@modules/tables/components/VTableFilter/types';
import type { IExtendedTableDataResponse, ITableColumnsOptions, ITableDataResponse, ITableDataResponseColumnOptions, ITableMultiselectActions, ITableState, ITableStateUpdateAction } from '@modules/tables/types';
import type { QueryObserverResult } from '@tanstack/vue-query';
import { useDownloadReport } from '@modules/download-report/composables';
import { useI18n } from '@modules/i18n/composables';
import { usePermissionsStore } from '@modules/permissions/store';
import { ESmartFilterColumnType } from '@modules/smart-filters/enums';
import { useTableData } from '@modules/tables/composables';

import type { IReviewPaymentSystemDetailsButtonsEmitsData } from '../components/VReviewPaymentSystemButtons/types';
import type { EPaymentSystemsTableListType, IAdminPaymentSystemsDetailsListRecord } from '../types';
import { PAYMENT_SYSTEM_DETAILS_PERMISSIONS, TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS } from '../constants';
import { useDownloadPaymentSystemListMutation } from '../mutations';
import { useAdminPaymentSystemsListQuery } from '../queries';
import { EMassReviewActionType, EReviewPaymentSystemActionType, isPaymentSystemName } from '../types';
import useConfirmPaymentSystem from './useConfirmPaymentSystem';
import useCurrentPaymentSystem from './useCurrentPaymentSystem';

export interface IPaymentSystemTableListComposable {
  rejectionReason: Ref<string | undefined>;
  data: Ref<IExtendedTableDataResponse<IAdminPaymentSystemsDetailsListRecord> | undefined>;
  tableState: Ref<ITableState>;
  isLoading: Ref<boolean>;
  isFetching: Ref<boolean>;
  updateTableState(state: Partial<ITableState>, action?: ITableStateUpdateAction): void;
  refreshTableData(): Promise<QueryObserverResult<ITableDataResponse<IAdminPaymentSystemsDetailsListRecord>, Error>>;
  onConfirmSingle(data: IReviewPaymentSystemDetailsButtonsEmitsData, actionType: EReviewPaymentSystemActionType): void;
  isMassConfirmLoading: Ref<boolean>;
  isSingleConfirmLoading: Ref<boolean>;
  isReportLoading: Ref<boolean>;
  onDownloadReport(): Promise<void>;
  columnOptions: ITableColumnsOptions;
  tableFilterOptions: ComputedRef<ITableFilterProps>;
  multiselectOptions: ComputedRef<ITableMultiselectActions>;
  handleMultiselectAction(data: ITableMultiselectActionParams): void;
}

export default function usePaymentSystemTableList(
  tableType: Ref<EPaymentSystemsTableListType>,
): IPaymentSystemTableListComposable {
  const rejectionReason = ref<string>();

  const { getPaymentSystem } = useCurrentPaymentSystem();
  const { hasPermission } = usePermissionsStore();
  const { getTranslation } = useI18n();
  const {
    onConfirmSingle,
    isSingleConfirmLoading,
    confirmMassAction,
    isMassConfirmLoading,
  } = useConfirmPaymentSystem(rejectionReason);

  const {
    tableState,
    isLoading,
    isFetching,
    data,
    updateTableState,
    refreshTableData,
  } = useTableData(useAdminPaymentSystemsListQuery, tableType);

  const {
    isReportLoading,
    onDownloadReport,
  } = useDownloadReport(useDownloadPaymentSystemListMutation(tableType), tableState);

  const columnOptions: ITableColumnsOptions = {
    id: {
      type: ESmartFilterColumnType.COMPARE,
    },
    new_payment_method_short: {
      type: ESmartFilterColumnType.CHECKBOX,
      ...getColumnsWithImageOptions('new_payment_name'),
    },
    old_payment_method_short: {
      type: ESmartFilterColumnType.CHECKBOX,
      ...getColumnsWithImageOptions('old_payment_name'),
    },
    status: {
      type: ESmartFilterColumnType.CHECKBOX,
    },
    affiliate_id: {
      type: ESmartFilterColumnType.COMPARE,
    },
    manager_id: {
      type: ESmartFilterColumnType.COMPARE,
    },
    editor_id: {
      type: ESmartFilterColumnType.COMPARE,
    },
    editor_name: {
      type: ESmartFilterColumnType.CHECKBOX,
    },
    affiliate_email: {
      type: ESmartFilterColumnType.CHECKBOX,
    },
    affiliate_name: {
      type: ESmartFilterColumnType.CHECKBOX,
    },
    affiliate_telegram: {
      type: ESmartFilterColumnType.CHECKBOX,
    },
    manager_name: {
      type: ESmartFilterColumnType.CHECKBOX,
    },
    created_at: {
      type: ESmartFilterColumnType.DATE,
    },
    updated_at: {
      type: ESmartFilterColumnType.DATE,
    },
  };

  const tableFilterOptions = computed<ITableFilterProps>(() => ({
    pathForFilter: `api2/admin/payment_change_forms_${tableType.value}`,
    actionForFilter: 'list',
    filterName: `payment_change_forms_${tableType.value}`,
  }));

  const multiselectOptions = computed<ITableMultiselectActions>(() => {
    const actions: ITableMultiselectActions = {};

    if (hasPermission(PAYMENT_SYSTEM_DETAILS_PERMISSIONS.UPDATE).value) {
      actions.approve = {
        label: getTranslation(TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS, `MASS_${EReviewPaymentSystemActionType.APPROVE}_BUTTON_LABEL`).value,
      };
    }

    if (hasPermission(PAYMENT_SYSTEM_DETAILS_PERMISSIONS.UPDATE).value) {
      actions.reject = {
        label: getTranslation(TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS, `MASS_${EReviewPaymentSystemActionType.REJECT}_BUTTON_LABEL`).value,
      };
    }

    return actions;
  });

  function handleMultiselectAction(data: ITableMultiselectActionParams): void {
    rejectionReason.value = undefined;

    switch (data.actionId) {
      case 'approve':
        confirmMassAction('confirmDialog', EReviewPaymentSystemActionType.APPROVE, {
          actionType: EMassReviewActionType.APPROVE,
          form_ids: data.selection,
          segment_id: tableState.value.filter_set_id,
          page: tableState.value.page || 1,
        });
        break;
      case 'reject':
        confirmMassAction('confirmDialogPaymentSystemReview', EReviewPaymentSystemActionType.REJECT, {
          actionType: EMassReviewActionType.REJECT,
          form_ids: data.selection,
          segment_id: tableState.value.filter_set_id,
          page: tableState.value.page || 1,
        });
        break;
      default:
        break;
    }
  }

  function getColumnsWithImageOptions(columnName: keyof IAdminPaymentSystemsDetailsListRecord): ITableDataResponseColumnOptions {
    return {
      allowedHtmlTags: ['img'],
      imageContainerClass: 'w-[40px] h-[28px] border rounded bg-white',
      imageSrc(value, column, rowData) {
        if (isPaymentSystemName(rowData?.[columnName])) {
          return getPaymentSystem(rowData?.[columnName])?.icon ?? '';
        }
        return '';
      },
    };
  };

  return {
    rejectionReason,
    data,
    tableState,
    isLoading,
    isFetching,
    updateTableState,
    refreshTableData,
    onConfirmSingle,
    isSingleConfirmLoading,
    isMassConfirmLoading,
    isReportLoading,
    onDownloadReport,
    columnOptions,
    tableFilterOptions,
    multiselectOptions,
    handleMultiselectAction,
  };
}
